import dayjs from "dayjs";
import { groupBy, orderBy } from "lodash";

export const ProductDetailSchema = (product, review, reviewSummary) => {
  let schema = {};

  const aggregateRating = {
    "@type": "AggregateRating",
    ratingValue: reviewSummary?.rating,
    reviewCount: reviewSummary?.total,
  };

  const includedAggregateRating = aggregateRating.reviewCount > 0;

  if (product?.productType?.hasVariants && product?.variants?.length > 0) {
    schema = configurableProductSchema(product);
  } else {
    schema = productSimpleSchema(product);
  }

  if (review?.data?.length > 0) {
    schema.review = getReviewsStructuredData(review.data);
  }

  if (includedAggregateRating) {
    schema.aggregateRating = aggregateRating;
  }

  return JSON.stringify(schema);
};

const configurableProductSchema = product => {
  const image = product?.images?.length > 0 && product.images[0].url;
  const brand = getBrandByAttributes(product.attributes);
  const schema = {
    "@context": "https://schema.org/",
    "@type": "ProductGroup",
    name: product?.name,
    description: product?.seoDescription,
    sku: product?.virtualSku,
    url: `${process.env.NEXT_PUBLIC_URL}/products/${product.slug}`,
    offers: productOfferSchema(product),
    hasVariant: [],
  };

  if (image) {
    schema.image = image;
  }

  product.variants.forEach((variant, index) => {
    const _productSchema = {
      "@type": "Product",
      name: product?.name,
      description: product?.seoDescription + ` (Variant ${index + 1})`,
      sku: variant?.sku,
      mpn: variant?.sku,
      url: `${process.env.NEXT_PUBLIC_URL}/products/${product.slug}`,
      offers: productOfferSchema(product, true),
      image: image,
      isVariantOf: {
        "@type": "ProductGroup",
        name: product?.name,
        sku: product?.virtualSku,
      },
      brand: {
        "@type": "Brand",
        name: brand,
      },
      category: product?.category?.name,
    };

    if (variant?.attributes?.length > 0) {
      const colorAttribute = variant?.attributes.find(item => item?.attribute?.name === "Colour");
      const sizeAttribute = variant?.attributes.find(item => item?.attribute?.name === "Sizes");

      if (colorAttribute && colorAttribute?.values?.length > 0) {
        _productSchema.color = colorAttribute.values[0].name;
        _productSchema.name += " | " + colorAttribute.values[0].name;

        //! Map colour image
        const { productColour } = useMultipleColour(product);
        if (productColour?.length) {
          const colorImageLink = productColour.find(item => item.name === colorAttribute.values[0].name);
          if (colorImageLink?.mainImage) {
            _productSchema.image = colorImageLink.mainImage;
          }
        }
      }

      if (sizeAttribute) {
        _productSchema.size = sizeAttribute.values[0].name;
        _productSchema.name += " | " + "Size " + sizeAttribute.values[0].name;
      } else if (!sizeAttribute && variant.name) {
        _productSchema.size = variant.name;
        _productSchema.name += " | " + "Size " + variant.name;
      }
    }

    schema["hasVariant"].push(_productSchema);
  });

  return schema;
};

const productSimpleSchema = product => {
  const image = product?.images?.length > 0 && product.images[0].url;
  const brand = getBrandByAttributes(product.attributes);
  const schema = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: product?.name,
    description: product?.seoDescription,
    sku: product?.virtualSku,
    mpn: product?.virtualSku,
    url: `${process.env.NEXT_PUBLIC_URL}/products/${product.slug}`,
    offers: productOfferSchema(product),
    brand: {
      "@type": "Brand",
      name: brand,
    },
    category: product?.category?.name,
  };
  if (image) {
    schema.image = image;
  }

  return schema;
};

const getReviewsStructuredData = reviews => {
  return (
    reviews &&
    Array.isArray(reviews) &&
    reviews.map(review => {
      return {
        "@type": "Review",
        author: {
          "@type": "Person",
          name: review.author,
        },
        datePublished: review.dateCreated,
        reviewBody: review?.body ?? review?.reviewMessage,
        reviewRating: {
          "@type": "Rating",
          ratingValue: review.reviewRating,
        },
      };
    })
  );
};

const productOfferSchema = (product, isVariant = false) => {
  const future = dayjs().add(1, "year").format("YYYY-MM-DD");
  let price, priceCurrency;

  if (isVariant) {
    const priceRange = product.pricing?.priceRange?.stop || product.pricing?.price;
    price = priceRange?.gross?.amount?.toFixed(2) ?? 0;
    priceCurrency = priceRange.gross.currency;
  } else {
    const priceRange = product.pricing?.priceRange?.stop || product.pricing?.priceRange;
    price = priceRange?.gross?.amount?.toFixed(2) ?? 0;
    priceCurrency = priceRange?.gross?.currency;
  }

  const schema = {
    "@type": "Offer",
    price,
    priceCurrency,
    availability: "https://schema.org/InStock",
    seller: {
      "@type": "Organization",
      name: "Bikebiz",
    },
  };

  if (product?.pricing?.onSale) {
    schema["priceValidUntil"] = future;
  }

  return schema;
};

const getBrandByAttributes = attributes => {
  if (!attributes) return;
  let branchName = "";
  const indexBrand = attributes.findIndex(item => item?.attribute?.name && item.attribute.name === "Manufacturer");

  if (indexBrand !== -1) {
    if (
      attributes[indexBrand].values &&
      Array.isArray(attributes[indexBrand].values) &&
      attributes[indexBrand].values.length > 0
    ) {
      branchName = attributes[indexBrand].values[0].name;
    }
  }

  return branchName;
};

function useMultipleColour(product) {
  let productColour = [];
  if (product.descriptionJson) {
    const colourDescription = JSON.parse(product.descriptionJson);
    productColour = orderBy(colourDescription.colour, "order");
  }
  const groupVariantsByColor = groupBy(product.variants, variant => {
    const colorAttribute = variant.attributes.find(attr => attr.attribute.name === "Colour");
    if (colorAttribute) {
      return colorAttribute.values[0]?.name;
    }
  });
  const defaultColor = productColour[0]?.name || Object.keys(groupVariantsByColor)[0];
  const initVariants =
    defaultColor && groupVariantsByColor[defaultColor] ? groupVariantsByColor[defaultColor] : product.variants;

  return { productColour, groupVariantsByColor, initVariants };
}
